import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Swaps the individual cart line items that make up a bundle group for the
 * matching bundle product (snippets/cart-bundle-upsells.liquid).
 *
 * Like cart-line-upsell, this can't use a nested <form> + submit because the
 * cart line items already live inside the cart's <form action="/cart">. So it
 * talks to the Ajax API directly in two steps and then dispatches a cart:update
 * with the re-rendered sections, which cart-items-component morphs in place:
 *   1. POST cart/add to add the bundle product (× the valid bundle quantity).
 *   2. POST cart/update to reduce each member line by the bundle quantity
 *      (lines that drop to 0 are removed). Only the member line keys are
 *      touched, so unrelated cart items — and any extra units of a member
 *      beyond the bundle quantity — are preserved.
 *
 * @typedef {object} CartBundleUpsellRefs
 * @property {HTMLButtonElement} button - The "Swap to bundle" button.
 * @property {HTMLScriptElement} [membersData] - JSON array of {key, quantity}.
 * @property {HTMLElement} [liveRegion] - Visually-hidden status region.
 *
 * @extends Component<CartBundleUpsellRefs>
 */
class CartBundleUpsellComponent extends Component {
  requiredRefs = ['button'];

  /** @param {Event} event */
  async upgrade(event) {
    event.preventDefault();

    const { button } = this.refs;
    if (button.disabled) return;

    const bundleVariantId = this.dataset.bundleVariantId;
    const bundleQty = parseInt(this.dataset.bundleQuantity ?? '1', 10) || 1;
    if (!bundleVariantId) return;

    /** @type {Array<{key: string, quantity: number}>} */
    let members = [];
    try {
      members = JSON.parse(this.refs.membersData?.textContent ?? '[]');
    } catch {
      members = [];
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    // Re-render every cart-items section on the page (cart page or drawer).
    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    }

    try {
      // 1. Add the bundle product.
      const addBody = new FormData();
      addBody.set('id', bundleVariantId);
      addBody.set('quantity', String(bundleQty));
      const addConfig = fetchConfig('javascript', { body: addBody });
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        ...addConfig,
        headers: { ...addConfig.headers, Accept: 'text/html' },
      }).then((response) => response.json());

      if (addResponse.status) {
        this.dispatchEvent(
          new CartErrorEvent(this.id, addResponse.message, addResponse.description, addResponse.errors)
        );
        window.publishStorefrontEvent?.('bundle_add_failed', {
          component: 'cart_bundle_upsell',
          offer_type: 'cart_bundle_swap',
          operation: 'add',
          error_type: 'server_rejected',
          response_status: addResponse.status || null,
        });
        button.disabled = false;
        button.removeAttribute('aria-busy');
        return;
      }

      // 2. Reduce each member line by the bundle quantity, asking for the
      //    refreshed sections so the cart re-renders in one pass.
      const updates = {};
      for (const member of members) {
        if (!member || !member.key) continue;
        const current = parseInt(String(member.quantity), 10) || 0;
        updates[member.key] = Math.max(0, current - bundleQty);
      }

      const updateResponse = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', {
          body: JSON.stringify({
            updates,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        })
      ).then((response) => response.json());

      if (this.refs.liveRegion) this.refs.liveRegion.textContent = Theme.translations?.added ?? '';

      // cart-items-component listens for this on `document` and morphs the cart
      // section, which replaces this component — no need to re-enable the button.
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'cart-bundle-upsell',
          sections: updateResponse.sections,
        })
      );
    } catch (error) {
      console.error(error);
      window.publishStorefrontEvent?.('bundle_replace_failed', {
        component: 'cart_bundle_upsell',
        offer_type: 'cart_bundle_swap',
        operation: 'swap',
        error_type: 'network',
      });
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cart-bundle-upsell-component')) {
  customElements.define('cart-bundle-upsell-component', CartBundleUpsellComponent);
}
